import { FormattingService } from '../services/formatting.service';
import { BreakpointsService } from '../services/breakpoints.service';
import { CellRange, ColDef, ColGroupDef, Column, ColumnPivotChangedEvent, GetContextMenuItemsParams, GridApi, GridOptions, GridReadyEvent, IAggFunc, IAggFuncParams, ICellRendererParams, IRowNode, MenuItemDef, RowNode } from 'ag-grid-community';
import _ from 'underscore';
import { IColumnFormatting, IColumnMetaData, IReportActionResultRows, IReportAggregates, IReportData, IReportMetadata, IReportRow, IReportValue, ISubColumns, ReportDataService } from './report-data.service';
import { Dictionary } from '@type_definitions/vendor';
import { util } from '../util';
import { DomUtil } from '../dom.util';
import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { GaDeferredService } from '../services/ga-deferred.service';
import { Observable, Subject } from 'rxjs';
import { IBrowselistActionPermission, IBrowseListConfig } from '../browselists/browselists-config.service';
import { IDescribedStringObject, IExtendedDocument, IExtendedWindow, IObject } from '@type_definitions/datatypes';
import { ActionStatus, ICellStyle, ReportDataType, ReportStyleService } from './report-style.service';
import { AggregateFunctionInput, IAggregateObject, ReportAggregationService } from './report-aggregation.service';
import { IColumnSort, ReportGrid } from './report-grid';
import { CellRendererComp } from '../ag-grid-components/ga-ag-grid.component';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '../ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { SHORT_MONTHS_ORDER } from '@app/shared/services/date.util';

type IColGroupDef = ColGroupDef;
export type GridSortType = 'asc' | 'desc';

export interface IColDef extends ColDef {
  dataType?:ReportDataType;
  screenType?:string;
  groupDef?:IColGroupDef;
  stripHtml?:boolean;
  autoHeightContext?:boolean;
}

export interface IReportSettingOption<T> {
  id:T;
  description:string;
}

enum SpecialColumnID {
  CHECK = 'check',
  INDEX = 'index',
  ACTIONS = 'actions',
  DUMMY =' dummy'
}

export enum ReportDisplayType {
  COMPACT = 'C',
  NORMAL = 'N'
}

export enum ReportColumnSizingType {
  PAGE = 'P',
  CONTENT = 'C',
  HEADER = 'H'
}

const EVENT_ENTER_BREAKPOINT_GRID:string = 'enter-bp.reportGrid';
const EVENT_RESIZE_GRID:string = 'resize.reportGrid';

@Injectable()
export class ReportGridService {
  private readonly defaultMinWidth:number = 100;
  private readonly defaultCheckColumnWidth:number = 56;
  private readonly defaultHeight:number = 36;
  private readonly defaultHeaderColumnPadding:number = 8; // column padding in pixels
  private readonly specialColumnIds:string[] = [SpecialColumnID.INDEX, SpecialColumnID.CHECK, SpecialColumnID.ACTIONS, SpecialColumnID.ACTIONS];
  private grids:IObject<ReportGrid> = {};
  private listening:boolean = false;
  private onLayoutSettingChangeSubject:Subject<string> = new Subject();
  private storedReportBusy:boolean = false;

  public onLayoutSettingChange:Observable<string> = this.onLayoutSettingChangeSubject.asObservable();
  public static SIZING_PAGE:IReportSettingOption<ReportColumnSizingType> = {
    description: 'Default',
    id: ReportColumnSizingType.PAGE
  };
  public static SIZING_CONTENT:IReportSettingOption<ReportColumnSizingType> = {
    description: 'Adjust to content',
    id: ReportColumnSizingType.CONTENT
  };
  public static SIZING_HEADER:IReportSettingOption<ReportColumnSizingType> = {
    description: 'Adjust to header',
    id: ReportColumnSizingType.HEADER
  };

  public get sizingOptions():IReportSettingOption<ReportColumnSizingType>[] {
   return [ ReportGridService.SIZING_PAGE, ReportGridService.SIZING_CONTENT, ReportGridService.SIZING_HEADER ];
  }

  public get reportDisplays():IReportSettingOption<ReportDisplayType>[] {
    return [
      { id: ReportDisplayType.NORMAL, description: 'Normal' },
      { id: ReportDisplayType.COMPACT, description: 'Compact' }
    ];
  }

  private get checkboxColumn():IColDef {
    return {
      colId: SpecialColumnID.CHECK,
      cellRenderer: (params:ICellRendererParams) => {
        if (params.node.isRowPinned()) {
          // set empty value to checkbox column for pinned rows (e.g. aggregates)
          return '';
        }

        return '<i class="fa fa-warning error-icon"></i><i class="fa fa-check-circle success-icon"></i>';
      },
      cellRendererParams: {
        checkbox: true
      },
      cellClass: 'check-col',
      headerName: '',
      maxWidth: this.defaultCheckColumnWidth,
      width: this.defaultCheckColumnWidth,
      pinned: 'left',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      suppressHeaderMenuButton: true,
      sortable: false,
      suppressSizeToFit: true,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      filter: false,
      lockPinned: true,
      lockPosition: true,
      lockVisible: true
    };
  }

  constructor(
    private breakpointsService:BreakpointsService,
    private reportStyleService:ReportStyleService,
    private formattingService:FormattingService,
    private deferredService:GaDeferredService,
    private reportAggregationService:ReportAggregationService,
    @Inject(DOCUMENT) private document:IExtendedDocument,
    @Inject(Window) private window:IExtendedWindow
  ) {
    /**/
  }

  private getColumnBrowselistActions(reportGrid:ReportGrid, deleteAllowed?:boolean, editAllowed?:boolean):IColDef {
    const gridId:string = reportGrid.id;
    return {
      colId: SpecialColumnID.ACTIONS,
      cellRenderer: (params:ICellRendererParams) => {
        if (params.node.isRowPinned()) {
          // set empty value to checkbox column for pinned rows (eg. aggregates)
          return '';
        }

        //do not use angular here, it will seriously impact performance
        const divElm:HTMLDivElement = this.document.createElement('div');
        divElm.className = 'ag-button-wrapper';

        const rowSelectable:boolean = this.isRowSelectable(params, reportGrid.browseListConfig);
        if (editAllowed && rowSelectable) {
          divElm.innerHTML =
            `<button class="btn btn-default btn-sm browselist-edit" title="${reportGrid.browseListConfig?.editButton?.label || 'Edit' }">
              ${reportGrid.browseListConfig?.editButton?.icon ?
                `<i class="fa ${reportGrid.browseListConfig?.editButton?.icon}"></i>` :
                util.isNullOrUndefinedOrEmpty(reportGrid.browseListConfig?.editButton) ? '<i class="fa fa-pencil"></i>' : ''}
              ${reportGrid.browseListConfig?.editButton?.label || '' }
            </button>`;
        }
        if (deleteAllowed && rowSelectable) {
          divElm.innerHTML +=
            `<button class="btn btn-default btn-sm browselist-delete" title="Delete">
              <i class="fa fa-trash-o"></i>
            </button>`;
        }

        const divAElm:JQuery<HTMLElement> = $(divElm);

        if (deleteAllowed && rowSelectable) {
          divAElm.find('.browselist-delete').on('click', () => {
            const reportGrid:ReportGrid = this.getReportGrid(gridId);
            reportGrid.deleteRow(params.node.rowIndex, reportGrid.reportTitle);
          });
        }
        if (editAllowed && rowSelectable) {
          divAElm.find('.browselist-edit').on('click', (e:JQueryInputEventObject) => {
            const reportGrid:ReportGrid = this.getReportGrid(gridId);
            reportGrid.editRow(params.node.rowIndex, e.currentTarget);
          });
        }
        return divElm;
      },
      cellClass: 'cell-actions',
      headerName: '',
      minWidth: 46,
      pinned: 'left',
      sortable: false,
      suppressHeaderMenuButton: true,
      suppressSizeToFit: false,
      suppressAutoSize: true,
      lockPinned: true,
      lockPosition: true,
      resizable: false
    };
  }

  public start(reportId:string, gridOptions:GridOptions, reportTitle:string, gridId:string = reportId):void {
    const reportGrid:ReportGrid = new ReportGrid(gridOptions, gridId);
    this.grids[ gridId ] = reportGrid;

    // set default column definitions of ag-grid
    gridOptions.defaultColDef = {};
    gridOptions.defaultColDef.sortable = true;
    gridOptions.defaultColDef.resizable = true;
    gridOptions.defaultColDef.filter = true;
    gridOptions.defaultColDef.filterParams = {
      convertValuesToStrings: true
    };

    gridOptions.rowHeight = this.defaultHeight;
    gridOptions.headerHeight = this.defaultHeight;
    gridOptions.overlayLoadingTemplate =
      '<div class="ag-overlay-loading-center"><i class="fa fa-spinner fa-spin"></i><div>loading...</div></div>';
    gridOptions.overlayNoRowsTemplate = gridOptions.overlayNoRowsTemplate ||
      '<div class="ag-overlay-no-rows-center"><h2>No results found</h2><div>Please change your filters and click "Search" to display results.</div></div>';

    gridOptions.context = {
      reportId,
      gridId
    };
    gridOptions.enableRangeSelection = true;
    gridOptions.rowSelection = 'multiple';
    gridOptions.groupSelectsChildren = true;

    // gridOptions.toolPanelSuppressSideButtons = true;
    gridOptions.enableRangeSelection = true;
    gridOptions.suppressPropertyNamesCheck = true;
    gridOptions.suppressRowClickSelection = true;
    gridOptions.getRowClass = this.reportStyleService.rowClassFunction;

    gridOptions.animateRows = true;
    gridOptions.enableCharts = true;

    if(gridOptions.sideBar !== false){
      gridOptions.sideBar = {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel'
          },
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel'
          }
        ]
      };
    }
    gridOptions.suppressMenuHide = true;
    gridOptions.pivotRowTotals = 'after';

    gridOptions.aggFuncs = {
      cnt: this.reportStyleService.distinctCountFunction,
      cnb: this.reportStyleService.countNonBlankFunction,
      concat: <any>this.reportAggregationService.aggregate.concat
    };
    gridOptions.suppressAggFuncInHeader = true;
    gridOptions.suppressCopyRowsToClipboard = true; // do not copy the full row to clipboard when there is a row selected
    gridOptions.processCellForClipboard = this.reportStyleService.cellClipboardFunction;
    gridOptions.getContextMenuItems = (params:GetContextMenuItemsParams):[ string | MenuItemDef ] => {
      const menu:[ string | MenuItemDef ] = [
        'copy'
      ];
      if (reportGrid.hasActions) {
        menu.push(
          {
            // copy row
            name: 'Copy Selected Rows',
            action: () => {
              return reportGrid.api?.copySelectedRowsToClipboard();
            },
            disabled: !reportGrid.api?.getSelectedRows().length
          }
        );
      }
      menu.push(
        'copyWithHeaders',
        'paste',
        'separator',
        {
          name: 'Content Size This Column',
          action: () => {
            reportGrid.api?.autoSizeColumns([params.column]);
          }
        },
        'separator',
        {
          name: 'Default Size All Columns',
          action: () => {
            this.setColumnSizing(params.context.gridId, ReportGridService.SIZING_PAGE);
          }
        },
        {
          name: 'Content Size All Columns',
          action: () => {
            this.setColumnSizing(params.context.gridId, ReportGridService.SIZING_CONTENT);
          }
        },
        {
          name: 'Header Size All Columns',
          action: () => {
            this.setColumnSizing(params.context.gridId, ReportGridService.SIZING_HEADER);
          }
        }
      );

      return menu;
    };

    // for row grouping default
    gridOptions.autoGroupColumnDef = {
      comparator: this.rowGroupingSortComparator,
      sortable: true,
      pinned: 'left',
      cellRenderer: CellRendererComp.GROUP_CELL_RENDERER,
      cellRendererParams: {
        innerRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        innerRendererParams: {
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            return [{
              label: params.node.key || params.valueFormatted || params.value
            }];
          }
        }
      }
    };

    reportGrid.overlays = [];
    reportGrid.reportReady = this.deferredService.defer<GridApi>();
    reportGrid.reportTitle = reportTitle;
  }

  private startListening():void {
    if (!this.listening) {
      this.listening = true;
      //listen for bp changes
      $(this.window).on(EVENT_ENTER_BREAKPOINT_GRID, this.changeColumnsVisibility);

      //listen for resize events
      $(this.window).on(EVENT_RESIZE_GRID, _.debounce(() => {
        this.setColumnSizing();
      }, 100));
    }
  }

  // resize column used by the browselist for the actions ( edit/customised edit button AND delete button), after the search is performed
  private resizeColumnBrowselistActions():void {
    _.each(this.grids, (reportGrid:ReportGrid) =>  {
      const columns:Column[] = _.filter(reportGrid.api?.getColumns(), (column:Column) => column.getColDef().colId === SpecialColumnID.ACTIONS);
      reportGrid.api?.autoSizeColumns(columns);
    });
  }

  public install(gridId:string, reportMetaData:IReportMetadata, hasCustomActions:boolean, reportTitle:string, browseListConfig?:IBrowseListConfig, browselistActionPermissions?:IBrowselistActionPermission):Promise<GridApi> {
    this.startListening();

    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    reportGrid.reportTitle = reportTitle;
    const gridOptions:GridOptions = reportGrid.gridOptions;

    reportGrid.reportMetaData = reportMetaData;
    // doesn't have action when browselist config is undefined or 'noDelete' is defined and true
    reportGrid.hasActions = (_.isUndefined(browseListConfig)? false : !browseListConfig.noDelete && browselistActionPermissions?.delete) || hasCustomActions;
    reportGrid.browseListConfig = browseListConfig;

    //wait until the grid is initialized
    gridOptions.onGridReady = (event:GridReadyEvent):void => {
      reportGrid.api = event.api;

      // resize the header when the columns contain text that has <br>
      this.resizeGridHeaderHeight(gridId);
      this.setColumnSizing(gridId);
      reportGrid.reportReady.resolve(reportGrid.api);

      if (reportGrid.hasActions) {

        reportGrid.api?.addEventListener('rangeSelectionChanged', () => {
          // allow only one range selection at a time
          const rangeSelections:CellRange[] = reportGrid.api?.getCellRanges();
          while (rangeSelections && rangeSelections.length > 1) {
            rangeSelections.shift();
          }
        });
      }

      this.registerAggregateFn(reportGrid);
    };

    reportGrid.gridOptions.onColumnMoved = () => {
      // when stored report is being applied, we should not interfere with column order
      // because this will cause issues when comparing between original report metadata nad original stored report metadata
      if (!this.storedReportBusy) {
        this.storeColumnOrder(reportGrid);
      }
      this.notifyLayoutSettingChange(reportGrid.id);
    };
    reportGrid.gridOptions.onColumnVisible = () => {
      this.storeColumnVisibility(reportGrid);
      this.notifyLayoutSettingChange(reportGrid.id);
    };
    reportGrid.gridOptions.onColumnValueChanged = () => {
      this.storeColumnValueAgg(reportGrid);
      this.notifyLayoutSettingChange(reportGrid.id);
    };
    reportGrid.gridOptions.onColumnRowGroupChanged = () => {
      this.storeColumnGroupOrder(reportGrid);
      this.notifyLayoutSettingChange(reportGrid.id);
    };
    reportGrid.gridOptions.onColumnPivotChanged = (event:ColumnPivotChangedEvent):void => {
      this.storeColumnPivotChange(reportGrid, event.api.getPivotColumns());
      this.notifyLayoutSettingChange(reportGrid.id);
    };

    // Triggered when: displayed rows are changed, group tree are expanded, a column is moved to a group, ...
    reportGrid.gridOptions.onModelUpdated = () => {
      this.autoSizeGroupedColumns(gridId);
    };

    this.installColumns(reportGrid, browselistActionPermissions);

    // allow to have a variation of rows in browselist, some rows selectable others not (no checkboxes)
    if (browseListConfig && !hasCustomActions) {
      reportGrid.gridOptions.isRowSelectable = (node:RowNode):boolean => this.isRowSelectable(node, browseListConfig);
    }

    // callback after a search is performed and the data is set to the grid
    reportGrid.gridOptions.onRowDataUpdated = () => {
      if (reportGrid.browseListConfig) {
        this.resizeColumnBrowselistActions();
      }
    };

    reportGrid.gridOptions.rowData = [];
    return reportGrid.reportReady.promise;
  }

  public setData(gridId:string, reportData:IReportData | string):any {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    if (!reportGrid) {
      return reportData;
    }

    if (reportData && reportData !== ReportDataService.EMPTY_MARKER) {
      const rd:IReportData = <IReportData>reportData;
      const hasAggregates:boolean = this.setAggregateData(reportGrid, rd);

      if (reportGrid.hasAggregates !== hasAggregates) {
        //add or remove aggregate column
        if (hasAggregates) {
          this.addIndexColumn(reportGrid);
        } else {
          this.removeIndexColumn(reportGrid);
        }
        reportGrid.hasAggregates = hasAggregates;
      }

      if (hasAggregates) {
        //add indexes to the data
        for (let i = 0; i < rd.data.length; i++) {
          rd.data[ i ].index = i + 1;
        }
      }

      reportGrid.reportData = rd.data;
      reportGrid.rowCount = rd.data?.length;
      this.doWhenGridReady(reportGrid, () => {
        reportGrid.api?.setGridOption('rowData', rd.data);
        this.setNoRows(reportGrid, !reportGrid.rowCount);
      });

    } else {
      reportGrid.reportData = [];
      this.doWhenGridReady(reportGrid, () => {
        reportGrid.api?.setGridOption('rowData', []);  //this automatically shows the 'no-rows' overlay
        this.setNoRows(reportGrid, false);
      });
    }
    return reportData;
  }

  public clearActionStatus(gridId:string):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    let shouldRefresh:boolean;

    _.forEach(reportGrid.reportData, (row:any) => {
      shouldRefresh = shouldRefresh || !!row.actionStatus;
      row.actionStatus = undefined;
    });

    if (shouldRefresh) {
      reportGrid.api?.redrawRows();
    }
  }

  public updateData(gridId:string, updatedRows:IReportActionResultRows):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    _.forEach(updatedRows, (updatedRow, idx) => {
      reportGrid.reportData[ idx ] = _.extend(reportGrid.reportData[ idx ], updatedRow.row);
      reportGrid.reportData[ idx ].actionStatus = updatedRow.actionStatus;
    });

    reportGrid.api?.redrawRows();
  }

  public addOrReplaceRows(gridId:string, rowsToAdd:IReportRow[], dataBindings?:IObject):number {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    const addSingleRowToGrid = (rowData:IObject):void => {
      reportGrid.reportData.push(rowData);
      reportGrid.api?.setGridOption('rowData', reportGrid.reportData);
      reportGrid.rowCount = reportGrid.reportData.length;
    };

    _.forEach(rowsToAdd, (savedRowData:IReportRow) => {
      // add action status (checkmark) when successfully added or updated
      savedRowData.actionStatus = ActionStatus.OK;

      if (util.notNullOrUndefinedOrEmpty(dataBindings)) {
        // create object by transforming data bindings property to real value
        const savedRowEntity:IObject = this.transformDataBindingsToValue(dataBindings, savedRowData);
        // check if data existed and get row index
        const existedRowIndex:number = _.findIndex(reportGrid.reportData, savedRowEntity);

        // since 'rowsToAdd' is an array parameter, we will compare per index with savedRowEntity if its existed
        // then if its existed, we will replace row data with 'savedRowData'
        if (existedRowIndex > -1) {
          // when exist then update data to the grid
          this.updateData(gridId, { [ this.getListIndex(gridId, existedRowIndex) ]: { row: savedRowData } });
        } else {
          // if not exist then add 'savedRowData' to the grid
          addSingleRowToGrid(savedRowData);
        }
      } else {
        // when 'dataBindings' not defined then just add data as it is
        addSingleRowToGrid(savedRowData);
      }
    });

    return reportGrid.rowCount;
  }

  /*
  ** @description transform dataBindings value to entityRow value
  ** @param dataBindings {IObject} - data bindings key properties
  ** @param entityRow {IObject} - selected row in report
  ** @param useKey {boolean} (default: false) - return object based on key defined in data bindings than column name
  ** @return object
   */
  public transformDataBindingsToValue(dataBindings:IObject, entityRow:IObject, useKey:boolean = false):IObject {
    const keys:IObject = {};

    if (entityRow) {

      _.forEach(dataBindings, (column:string, key:string) => {
        const cell:any = entityRow[ column ] || entityRow[ key ];

        if (useKey) {
          // if useKey is true then ignore idKey as object property
          if (key !== 'idKey') {
            keys[ key ] = util.extractValue(cell);
          }
        } else {
          keys[ column ] = util.extractValue(cell);
        }
      });
    }

    return keys;
  }

  private setAggregateData(reportGrid:ReportGrid, reportData:IReportData):boolean {
    const aggRows:any[] = [];

    _.each(<IObject>reportData.aggregates, (values:any, name:string) => {
      values.index = util.capitalize(name);
      aggRows.push(values);
    });
    this.doWhenGridReady(reportGrid, () => {
      reportGrid.api?.setGridOption('pinnedTopRowData', aggRows);
    });
    return !!aggRows.length;
  }

  public getSelection(gridId:string):any {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    if (!reportGrid.api?.getSelectedRows().length) {
      return undefined;
    }

    const selection:IObject = {};
    const selectedRows:any[] = reportGrid.api?.getSelectedRows();

    reportGrid.api?.forEachNodeAfterFilterAndSort((row:IRowNode) => {
      const rowData = row.data;
      if (_.contains(selectedRows, rowData)) { //is it selected?
        //find it in the data
        const idx:number = _.findIndex(reportGrid.reportData, rowData);
        selection[ idx ] = rowData;
      }
    });

    return selection;
  }

  public getSelectedRowIndices(gridId:string):number[] {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    return _.map(<IObject>reportGrid.api?.getSelectedNodes(), (rowNode:RowNode) => {
      return rowNode.rowIndex; // when the column group the order of index change (because of parent group row) use rowIndex instead 'childIndex'
    });
  }

  public stop():void {
    this.stopListening();
    this.grids = {};
  }

  private stopListening():void {
    if (this.listening) {
      this.listening = false;
      $(this.window).off(EVENT_ENTER_BREAKPOINT_GRID);
      $(this.window).off(EVENT_RESIZE_GRID);
    }
  }

  public setEditButtonDisabled(editBtnElement:HTMLButtonElement, disabled:boolean):void {
    editBtnElement.disabled = disabled;

    if (disabled) {
      $(editBtnElement).find('i')
        .removeClass('fa-pencil')
        .addClass('fa-spinner fa-spin');
    } else {
      $(editBtnElement).find('i')
        .removeClass('fa-spinner fa-spin')
        .addClass('fa-pencil');
    }
  }

  public setLoading(gridId:string, show:boolean):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    if (!reportGrid) {
      return;
    }

    if (show) {
      reportGrid.overlays = reportGrid.overlays?.filter(s => s !== 'loading').concat([ 'loading' ]);
    } else {
      reportGrid.overlays = reportGrid.overlays?.filter(s => s !== 'loading');
    }
    this.changeOverlays(reportGrid);
  }

  private setNoRows(reportGrid:ReportGrid, noRows:boolean):void {
    if (noRows) {
      reportGrid.overlays = reportGrid.overlays.filter(s => s !== 'noRows').concat([ 'noRows' ]);
    } else {
      reportGrid.overlays = reportGrid.overlays.filter(s => s !== 'noRows');
    }
    this.changeOverlays(reportGrid);
  }

  private doWhenGridReady(reportGrid:ReportGrid, fn:(() => void | Promise<void>)):void {
    if (reportGrid.api) {
      fn();
    } else {
      reportGrid.reportReady.promise
        .then(fn);
    }
  }

  private changeOverlays(reportGrid:ReportGrid):void {
    this.doWhenGridReady(reportGrid, () => {
      if (reportGrid.overlays.indexOf('loading') >= 0) {
        reportGrid.api?.showLoadingOverlay();
      } else if (reportGrid.overlays.indexOf('noRows') >= 0) {
        reportGrid.api?.showNoRowsOverlay();
      } else {
        reportGrid.api?.hideOverlay();
      }
    });
  }

  public setStoredReportBusy(busy:boolean):void {
    this.storedReportBusy = busy;
  }

  // resize the header when <br> is found in the header labels
  public resizeGridHeaderHeight = (gridId?:string):void => {
    // when no gridId is received take into account all grids
    const reportGrids:ReportGrid[] = gridId? [ this.getReportGrid(gridId) ] : _.values(this.grids);

    // set the new header height for each grid
    reportGrids.forEach((reportGrid:ReportGrid) => {
      this.setGridHeaderHeight(reportGrid, this.getMaxLinesFromColumns(reportGrid));
    });
  };

  // count the maximum number of lines from all columns of a given grid
  private getMaxLinesFromColumns(reportGrid:ReportGrid):number {
    let brCounterMax:number = 0;
    if (reportGrid.reportMetaData && reportGrid.reportMetaData.columns) {
      _.each(reportGrid.reportMetaData.columns, (colMetaData:IColumnMetaData) => {
        const brMatch:RegExpMatchArray = colMetaData.label && colMetaData.label.match(/<br ?\/?>/gi);

        // take into account only visible columns, which have defined a label and matches the pattern (has <br>)
        if (this.isColumnVisible(colMetaData) && colMetaData.label && brMatch && brMatch.length > brCounterMax) {
          brCounterMax = brMatch.length;
        }
      });
    }

    // the number of lines is equal with the number of br tags + 1
    return brCounterMax + 1;
  }

  // set the header height of a grid based on a given number of lines
  private setGridHeaderHeight(reportGrid:ReportGrid, numberOfLines:number):void {
    if (!reportGrid.api) {
      return;
    }

    // remove column padding
    let newHeaderHeight:number = this.defaultHeight - (this.defaultHeaderColumnPadding * 2);
    // multiply the column height with the number of <br> found in the label + 1
    if (numberOfLines > 1) {
      newHeaderHeight *= numberOfLines;
    }
    // add back the padding
    newHeaderHeight += this.defaultHeaderColumnPadding * 2;

    // set the new height to the header
    if (reportGrid.gridOptions.headerHeight && reportGrid.gridOptions.headerHeight !== newHeaderHeight) {
      reportGrid.api?.setGridOption('headerHeight', newHeaderHeight);
    }
  }

  public setAggregates(gridId:string):void {
    this.notifyLayoutSettingChange(gridId);
  }

  // set the width of the columns
  public setColumnSizing = (gridId?:string, autoSizeColumns?:IDescribedStringObject, notifyChange?:boolean):void => {
    const reportGrids:ReportGrid[] = gridId? [ this.getReportGrid(gridId) ] : _.values(this.grids);

    reportGrids.forEach((reportGrid:ReportGrid) => {

      if (!reportGrid.api) {
        return;
      }

      if (util.notNullOrUndefinedOrEmpty(autoSizeColumns)) {
        reportGrid.autoSizeColumns = autoSizeColumns;
      }

      if (reportGrid.autoSizeColumns.id === ReportGridService.SIZING_PAGE.id) {
        reportGrid.api?.sizeColumnsToFit();
      } else if (reportGrid.autoSizeColumns.id === ReportGridService.SIZING_CONTENT.id) {
        // only resize columns that aren't pinned (e.g. checkbox col)
        const columns:Column[] = _.reject(reportGrid.api?.getColumns(), (column:Column) => <boolean>column.getColDef().pinned);
        reportGrid.api?.autoSizeColumns(columns);
      } else if (reportGrid.autoSizeColumns.id === ReportGridService.SIZING_HEADER.id) {
        const paddingEtc:number = 52 + 1;
        const menuWidth:number = 0;

        const columnDefs =
          _.chain(reportGrid.api?.getColumns())
            //we need the original column definitions
            .map((c:Column) => <IColDef>c.getColDef())
            // only resize columns that aren't pinned (e.g. checkbox col)
            .reject((colDef:IColDef) => <boolean>colDef.pinned)
            //flatten the tree of columns
            .reduce(
              (memo:any[], colDef:IColDef | IColGroupDef) => (<IColGroupDef>colDef).children? memo.concat((<IColGroupDef>colDef).children) : memo.concat([ colDef ]),
              [])
            .value();

        columnDefs.forEach(colDef => {
          const innerHtml:string = headerName(colDef);
          let colWidth:number = measure(innerHtml);
          colWidth += paddingEtc + (colDef.suppressMenu? 0 : menuWidth);
          if (colDef.minWidth && colWidth < colDef.minWidth) {
            colWidth = colDef.minWidth;
          }
          if (colDef.maxWidth && colWidth > colDef.maxWidth) {
            colWidth = colDef.maxWidth;
          }
          reportGrid.api?.setColumnWidths([{ key: colDef.colId, newWidth: colWidth }]);
        });
      }

    });

    if (notifyChange) {
      // notify change
      this.notifyLayoutSettingChange(gridId);
    }

    function headerName(colDef:IColDef) {
      return colDef.headerName || (colDef.groupDef && colDef.groupDef.headerName);
    }

    function measure(innerHtml:string):number {
      return DomUtil.measureText(innerHtml, 'ag-header-measure', {
          'font-weight': 'bold',
          'font-size': '14px'
        }
      );
    }
  };

  // auto resize the width of the columns that are currently in a group
  public autoSizeGroupedColumns = (gridId?:string) => {
    const reportGrids:ReportGrid[] = gridId? this.getReportGrid(gridId) && [ this.getReportGrid(gridId) ] : _.values(this.grids);
    _.each(reportGrids, (reportGrid:ReportGrid) => {
      if (!reportGrid.api) {
        return;
      }

      if (reportGrid.reportData?.length) {
        const colGroup:Column[] = _.filter(reportGrid.api?.getAllGridColumns(), (column:Column) => !!column.getColDef().showRowGroup);
        if (colGroup && colGroup.length) {
          reportGrid.api?.autoSizeColumns(colGroup);
        }
      }
    });
  };

  public setDisplayType(gridId:string, displayType?:string):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    // When the API is not available, this should do nothing. This typically occurs in a unit test environment because the ag-Grid API is not yet initialized.
    // The API will always be available in production code, as it is ready after the 'onGridReady' event is triggered.
    if (!reportGrid?.api) {
      return;
    }

    const rowHeight:number = displayType === ReportDisplayType.COMPACT ? (this.defaultHeight - 4) : this.defaultHeight;
    const checkColumnWidth:number = displayType === ReportDisplayType.COMPACT ? (this.defaultCheckColumnWidth - 11) : this.defaultCheckColumnWidth;
    const minWidth:number = displayType === ReportDisplayType.COMPACT ? (this.defaultMinWidth - 20) : this.defaultMinWidth;

    // Notes regarding comment points 1 and 4:
    // We need to toggle the 'autoHeight' column property twice to ensure the original row height aligns with the newly set display type.
    // Toggling twice is necessary because ag-Grid recalculates rowHeight based on the 'autoHeight' property in certain columns,
    // taking into account the default value defined in rowHeight.

    // 1.) Update minimum width of columns and set all columns autoHeight to false, we want to make sure that rowHeight applied correctly
    const colDefs:IColDef[] = reportGrid.api.getGridOption('columnDefs');
    const hasAutoHeightContext:boolean = !!_.filter(colDefs, (colDef:IColDef) => colDef.autoHeightContext)?.length;

    _.each(colDefs, (colDef:IColDef) => {
      colDef.minWidth = minWidth;
      colDef.autoHeight = false;
    });

    // 2.) Update column definitions, header height, rowHeight, and check column width
    reportGrid.api.setGridOption('columnDefs', colDefs);
    reportGrid.api.setGridOption('headerHeight', rowHeight);
    reportGrid.api.setGridOption('rowHeight', rowHeight);
    reportGrid.api.setColumnWidths([{ key: SpecialColumnID.CHECK, newWidth: checkColumnWidth }]);

    // 3.) Reset row heights with new applied settings
    reportGrid.api.resetRowHeights();

    // 4.) Update column definitions and reset row heights with autoHeight settings if any
    if (hasAutoHeightContext) {
      const colDefs:IColDef[] = reportGrid.api.getGridOption('columnDefs');

      _.each(colDefs, (colDef:IColDef) => {
        colDef.autoHeight = !!colDef.autoHeightContext;
      });

      reportGrid.api.setGridOption('columnDefs', colDefs);
      reportGrid.api.resetRowHeights();
    }

    this.notifyLayoutSettingChange(gridId);
  }

  private static getColId(col:IColumnMetaData | string, colGroup?:IColumnMetaData):string {
    let id:string = colGroup ? `${colGroup.id}_` : '';
    if (colGroup) {
      id += `${colGroup.id}_`;
    }
    return id + (typeof col === 'string'? col : (<IColumnMetaData>col).id);
  }

  private createSimpleColumn(reportGrid:ReportGrid, colMetaData:IColumnMetaData, groupDef:IColGroupDef, colId:string, headerName:string, hide:boolean, cellStyle:ICellStyle):IColDef {
    const formatting:IColumnFormatting = colMetaData.formatting || {};

    const columnSort:IColumnSort = _.findWhere(reportGrid.sortModel, { name: colMetaData.name });
    const sortIndex:number = columnSort? _.indexOf(reportGrid.sortModel, columnSort) : undefined;
    const sort:GridSortType = columnSort? columnSort.direction : undefined;

    const colDef:IColDef = {
      colId,
      groupDef,
      dataType: colMetaData.type,
      screenType: colMetaData.screenType,
      headerName,
      headerClass: cellStyle.cellClass,
      field: colMetaData.name,
      filterParams: {},
      hide,
      valueFormatter: cellStyle.valueFormatter,
      valueGetter: cellStyle.valueGetter,
      comparator: cellStyle.comparator,
      keyCreator: cellStyle.keyCreator,
      cellClass: cellStyle.cellClass,
      cellClassRules: cellStyle.cellClassRules,
      menuTabs: [ 'filterMenuTab' ],
      minWidth: formatting.minWidth || this.defaultMinWidth,
      maxWidth: formatting.maxWidth,
      stripHtml: formatting.stripHtml,
      sort,
      sortIndex
    };

    if (_.isFunction(cellStyle.cellRenderer)) {
      colDef.cellRenderer = (params:ICellRendererParams) => {
        if (params.node.isRowPinned()) {
          // for pinned rows
          return this.reportStyleService.aggregateRenderer(params);
        }

        return cellStyle.cellRenderer(params);
      };
    } else {
      colDef.cellRenderer = cellStyle.cellRenderer;
      colDef.cellRendererParams = cellStyle.cellRendererParams;
    }

    if (formatting.autoHeight) {
      (<IColDef>colDef).autoHeightContext = true;
      (<IColDef>colDef).autoHeight = true;
      (<IColDef>colDef).cellClass = [
        (<string>cellStyle.cellClass),
        'multi-line-cell'
      ];
    }

    if (!formatting.headerdropdown && colMetaData.type !== 'DATE') {
      (<IColDef>colDef).suppressHeaderMenuButton = true;
      (<IColDef>colDef).filter = false;
    } else {
      const menuWidth:number = 20;
      (<IColDef>colDef).minWidth += menuWidth;
      if ((<IColDef>colDef).maxWidth) {
        (<IColDef>colDef).maxWidth += menuWidth;
      }
    }

    // grouping and aggregates
    // is aggregation allowed?
    colDef.allowedAggFuncs = this.getAllowedAggFuncs(colMetaData.aggregate);
    colDef.enableValue = !!colDef.allowedAggFuncs.length;

    // is grouping allowed?
    colDef.enableRowGroup = formatting.headerdropdown;
    if (colMetaData.type === 'DATE') {
      colDef.enableRowGroup = true;
    }

    // Grouping
    if (util.notNullOrUndefinedOrEmpty(colMetaData.groupingSequence)) {
      colDef.rowGroup = true;
      colDef.rowGroupIndex = colMetaData.groupingSequence;
      colDef.pinned = 'left';
      colDef.hide = true;
    }

    // is pivot allowed?
    colDef.enablePivot = formatting.headerdropdown;

    // Pivot
    if (util.notNullOrUndefinedOrEmpty(colMetaData.pivotSequence)) {
      colDef.pivot = true;
      colDef.pivotIndex = colMetaData.pivotSequence;
      reportGrid.api?.setGridOption('pivotMode', true);
    }

    if (colDef.allowedAggFuncs.indexOf(colMetaData.aggregateOn) !== -1) {
      colDef.aggFunc = colMetaData.aggregateOn;
    }
    return colDef;
  }

// converts IReportAggregates into a list of ag-grid aggregation functions
  private getAllowedAggFuncs(aggregates:IReportAggregates):string[] {
    const allowedAggFuncs:string[] = [];
    _.forEach(<any>aggregates, (value:boolean, key:string) => {
      if (value) {
        switch (key) {
          case 'average':
            allowedAggFuncs.push('avg');
            break;
          case 'count':
            allowedAggFuncs.push('cnt');
            break;
          default:
            allowedAggFuncs.push(key);
        }
      }
    });
    allowedAggFuncs.push('concat');
    allowedAggFuncs.push('cnb');
    return allowedAggFuncs;
  }

  private installColumns(reportGrid:ReportGrid, browselistActionPermission?:IBrowselistActionPermission):void {
    const columnDefs:Array<IColDef> = [];
    const currentBP:string = this.breakpointsService.current();

    if (reportGrid.hasActions) { //add a checkbox column
      columnDefs.push(this.checkboxColumn);
    }

    if (reportGrid.browseListConfig) {
      const allowDelete:boolean = !reportGrid.browseListConfig?.noDelete && browselistActionPermission.delete;
      const allowEdit:boolean = !reportGrid.browseListConfig?.noEdit && browselistActionPermission.edit;

      // if user has permission either delete / edit then add edit column
      if (allowDelete || allowEdit) {
        columnDefs.push(this.getColumnBrowselistActions(reportGrid, allowDelete, allowEdit));
      }
    }

    _.chain(reportGrid.reportMetaData?.columns)
      .sortBy((colMetaData:IColumnMetaData) => colMetaData.name)
      .sortBy((colMetaData:IColumnMetaData) => colMetaData.listSequence)
      .forEach((colMetaData:IColumnMetaData) => {
        // create a formatted label in which are removed the br tags
        colMetaData.formattedLabel = this.formattingService.removeHtmlBreaks(colMetaData.label, '\n');

        const formatting:IColumnFormatting = colMetaData.formatting || {};
        if (!formatting.showinlist) {
          return;
        }

        const cellStyle:ICellStyle = this.reportStyleService.getCellStyle(colMetaData);
        const hide:boolean = !colMetaData.formatting.defaultinlist || !ReportGridService.columnVisible(colMetaData, currentBP);

        let colDef:IColDef | IColGroupDef;
        if (!colMetaData.hasSubColumns) {
          colDef = this.createSimpleColumn(reportGrid, colMetaData, undefined, ReportGridService.getColId(colMetaData), colMetaData.formattedLabel, hide, cellStyle);
        } else {
          colDef = {
            headerName: colMetaData.formattedLabel,
            marryChildren: true
          };

          if (reportGrid.subColumns[ colMetaData.label ]) { //we already know the subColumns => add the list
            (<IColGroupDef>colDef).children =
              _.map(reportGrid.subColumns[ colMetaData.label ], (subColumnMetaData:IColumnMetaData) =>
                this.createSimpleColumn(reportGrid, subColumnMetaData, <IColGroupDef>colDef, ReportGridService.getColId(subColumnMetaData, colMetaData), subColumnMetaData.label, hide, cellStyle)
              );
          } else { //create a single child column to make sure the group will be visible

            (<IColGroupDef>colDef).children = [
              this.createSimpleColumn(reportGrid, colMetaData, <IColGroupDef>colDef, ReportGridService.getColId(SpecialColumnID.DUMMY, colMetaData), '', hide, cellStyle)
            ];
          }
        }
        // apply the render to the column header
        colDef.headerName = this.reportStyleService.headerRenderer(colDef);

        const customAggregationFunction:IAggFunc = this.reportAggregationService.getCustomAggregationFunction(reportGrid.reportMetaData.id, colMetaData.name);
        if (customAggregationFunction) {
          (<IColDef>colDef).aggFunc = customAggregationFunction;
        }

        columnDefs.push(colDef);
      });

    if (reportGrid.api) { // the grid has already started
      reportGrid.api?.setGridOption('columnDefs', columnDefs);
      this.setColumnSizing(reportGrid.id);
    } else { // the grid has not yet started
      reportGrid.gridOptions.columnDefs = columnDefs;
    }
  }

  private addIndexColumn(reportGrid:ReportGrid):void {
    const indexColDef:IColDef = {
      colId: SpecialColumnID.INDEX,
      headerName: '',
      field: 'index',
      cellClass: 'index-col',
      sortable: false,
      suppressHeaderMenuButton: true,
      pinned: 'left',
      width: 80,
      minWidth: 80,
      maxWidth: 80
    };

    // checkbox col, index col, other cols
    this.doWhenGridReady(reportGrid, () => {
      const currentColDefs:IColDef[] = this.getCurrentColumnDefs(reportGrid);
      let newColDefs:Array<IColDef> = _.filter(currentColDefs, (cd:IColDef) => cd.colId === SpecialColumnID.CHECK);
      newColDefs.push(indexColDef);
      newColDefs = newColDefs.concat(_.filter(currentColDefs, (cd:IColDef) => (cd.colId !== SpecialColumnID.CHECK) && cd.colId.indexOf('AutoColumn') < 0));

      reportGrid.api?.setGridOption('columnDefs', newColDefs);
      this.setColumnSizing(reportGrid.id);
    });
  }

  private getCurrentColumnDefs(reportGrid:ReportGrid):IColDef[] {
    return _.map(reportGrid.api?.getColumns(), (column:Column) => column.getColDef());
  }

  private removeIndexColumn(reportGrid:ReportGrid):void {
    this.doWhenGridReady(reportGrid, () => {
      const columnDefs:Array<IColDef> = _.filter(this.getCurrentColumnDefs(reportGrid), (cd:IColDef) => cd.colId !== SpecialColumnID.INDEX);
      reportGrid.api?.setGridOption('columnDefs', columnDefs);
      this.setColumnSizing(reportGrid.id);
    });
  }

  // we overwrite the default aggregates with ours which extract child values
  private registerAggregateFn(reportGrid:ReportGrid):void {
    _.each(<IObject>this.reportAggregationService.aggregate, (aggFn:AggregateFunctionInput<number|IAggregateObject|string>, name:string) => {
      reportGrid.api?.addAggFuncs({ [name]: (params:IAggFuncParams) => this.formatAggregateValues(params.values, aggFn) });
    });
  }

  // the aggregates don't work when the cell value is received as child. We are using this method to extract the child value
  private formatAggregateValues = (values:(number | IReportValue)[], aggFn:Function):number => {
    const listValues:number[] = [];
    values.forEach((value:IReportValue | number) => {
      if (value) {
        const isObject:boolean = _.isObject(value) && (<IReportValue>value).properties;
        listValues.push(isObject? (<number>(<IReportValue>value).value) : <number>value);
      }
    });
    // calculate the corresponding aggregate function
    return aggFn(listValues);
  };

  public getRowCount(gridId:string):Observable<number> {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    return reportGrid.rowCountSubscriber;
  }

  public getRowData(gridId:number | string, visibleIndex:number):any {
    if (util.isNullOrUndefinedOrEmpty(visibleIndex)) {
      return;
    }

    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    return reportGrid.api?.getDisplayedRowAtIndex(visibleIndex).data;
  }

  public getListIndex(gridId:string, visibleIndex:number):number {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    const rowData = this.getRowData(gridId, visibleIndex);
    return reportGrid.reportData.indexOf(rowData);
  }

  /**
   * Returns a hashtable of column names with their index on screen
   */
  public getVisibleIndices(gridId:string):Dictionary<number> {
    const visibleIndices:Dictionary<number> = {};
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    _.chain(reportGrid.api?.getAllGridColumns())
      // filter out all the 'fake' columns
      .filter((column:Column) => !_.contains(this.specialColumnIds, column.getColId()))
      .forEach((column:Column, idx:number) => {
        visibleIndices[ (<ColDef>column.getDefinition()).field ] = idx;
      });

    return visibleIndices;
  }

  //update report metadata to reflect the changes
  private storeColumnOrder(reportGrid:ReportGrid):void {
    _.chain(reportGrid.api?.getAllGridColumns())
      // filter out all the 'fake' columns
      .filter((column:Column) => !_.contains(this.specialColumnIds, column.getColId()))
      .forEach((column:Column, idx:number) => {
        const id:string = (<ColDef>column.getDefinition()).colId;
        const colMetadata:IColumnMetaData = _.find(reportGrid.reportMetaData.columns, (colMetadata:IColumnMetaData) => colMetadata.id === id);

        if (colMetadata) {
          colMetadata.listSequence = idx;
        }
      });
  }

  // update report metadata to reflect the column visibility
  private storeColumnVisibility(reportGrid:ReportGrid):void {
    _.chain(reportGrid.api?.getColumns())
      // filter out all the 'fake' columns
      .filter((column:Column) => !_.contains(this.specialColumnIds, column.getColId()))
      .forEach((column:Column) => {
        const id:string = (<ColDef>column.getDefinition()).colId;
        const colMetadata:IColumnMetaData = _.find(reportGrid.reportMetaData.columns, (colMetadata:IColumnMetaData) => colMetadata.id === id);

        if (colMetadata) {
          colMetadata.formatting.defaultinlist = column.isVisible();
        }
      });
  }

  public restoreColumnsVisibility(gridId:string, metadata:IReportMetadata):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    _.forEach(reportGrid.reportMetaData.columns, (colMetadata:IColumnMetaData) => {
      const backupColMetadata:IColumnMetaData = _.find(metadata.columns, (bColMetadata:IColumnMetaData) => bColMetadata.id === colMetadata.id);

      if (backupColMetadata) {
        colMetadata.formatting.defaultinlist = backupColMetadata.formatting.defaultinlist;
      }
    });
  }

  // update report metadata to reflect changes on grouping sequence
  private storeColumnGroupOrder(reportGrid:ReportGrid):void {

    // reset all grouping sequence
    _.forEach(reportGrid.api?.getAllGridColumns(), (column:Column) => {
      const id:string = (<ColDef>column.getDefinition()).colId;
      const colMetadata:IColumnMetaData = _.find(reportGrid.reportMetaData.columns, (colMetadata:IColumnMetaData) => colMetadata.id === id);

      if (colMetadata) {
        colMetadata.groupingSequence = undefined;
      }
    });

    // set metadata
    _.forEach(reportGrid.api?.getRowGroupColumns(), (column:Column, index:number) => {
      const id:string = (<ColDef>column.getDefinition()).colId;

      _.forEach(reportGrid.reportMetaData.columns, (colMetadata:IColumnMetaData) => {
        if (colMetadata.id === id) {
          colMetadata.groupingSequence = index;
        }
      });
    });
  }

  // update report metadata to reflect changes on aggregateFunction
  private storeColumnValueAgg(reportGrid:ReportGrid):void {

    // reset all aggregates
    _.forEach(reportGrid.api?.getAllGridColumns(), (column:Column) => {
      const id:string = (<ColDef>column.getDefinition()).colId;
      const colMetadata:IColumnMetaData = _.find(reportGrid.reportMetaData.columns, (colMetadata:IColumnMetaData) => colMetadata.id === id);

      if (colMetadata) {
        colMetadata.aggregateOn = undefined;
      }
    });

    // set metadata
    _.forEach(reportGrid.api?.getValueColumns(), (column:Column) => {
      const id:string = (<ColDef>column.getDefinition()).colId;

      _.forEach(reportGrid.reportMetaData.columns, (colMetadata:IColumnMetaData) => {
        if (colMetadata.id === id) {
          colMetadata.aggregateOn = <string>column.getAggFunc();
        }
      });
    });
  }

  private storeColumnPivotChange = (reportGrid:ReportGrid, pivotColumns:Column[]):void => {
    // reset all pivot sequence
    _.each(reportGrid.reportMetaData.columns, (column:IColumnMetaData) => {
      column.pivotSequence = undefined;
    });

    _.each(pivotColumns, (column:Column, index:number) => {
      const id:string = (<ColDef>column.getDefinition()).colId;

      const colMetadata:IColumnMetaData = _.find(reportGrid.reportMetaData.columns, (colMetadata:IColumnMetaData) => colMetadata.id === id);

      if (colMetadata) {
        colMetadata.pivotSequence = index;
      }
    });
  };

  // remove one or multiple rows from ag-grid
  public removeRowData(gridId:string, visibleIndex:number | number[]):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    const rowIds:number[] = _.isArray(visibleIndex)? visibleIndex : [ visibleIndex ];

    _.each(rowIds, (rowId:number) => {
      reportGrid.reportData.splice(this.getListIndex(gridId, rowId), 1);
    });

    reportGrid.api?.setGridOption('rowData', reportGrid.reportData);
  }

  public setRowStatus(gridId:string, visibleIndex:number, status:string):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    const reportRow:IReportRow = this.getRowData(gridId, visibleIndex);
    reportRow.actionStatus = status;
    reportGrid.api?.redrawRows();
  }

  public updateColumnsVisibility(gridId:string):void {
    this.changeColumnsVisibility(undefined, this.breakpointsService.current(), gridId);
  }

  private changeColumnsVisibility = (_event:Event, currentBP:string, gridId?:string):void => {
    const reportGrids:ReportGrid[] = gridId? [ this.getReportGrid(gridId) ] : _.values(this.grids);
    reportGrids.forEach((reportGrid:ReportGrid) => {
      if (!reportGrid.api) {
        return;
      }

      const setColumnVisible = (colId:string, visible:boolean) => {
        const column:Column = reportGrid.api?.getColumn(colId);
        if (column && column.isVisible() !== visible) {
          reportGrid.api?.setColumnsVisible([colId], visible);
        }
      };

      _.forEach(reportGrid.reportMetaData && reportGrid.reportMetaData.columns, (col:IColumnMetaData) => {
        if (col.formatting) {
          const visible = col.formatting.defaultinlist && col.formatting.showinlist && ReportGridService.columnVisible(col, currentBP);
          if (col.hasSubColumns) {
            if (reportGrid.subColumns[ col.label ] && reportGrid.subColumns[ col.label ].length) {
              //we already know the sub columns -> hide/show them all
              _.forEach(reportGrid.subColumns[ col.label ],
                (subCol:IColumnMetaData) => {
                  setColumnVisible(ReportGridService.getColId(subCol, col), visible);
                }
              );
            } else {
              //we don't know the sub columns -> hide/show the dummy sub column
              setColumnVisible(ReportGridService.getColId(SpecialColumnID.DUMMY, col), visible);
            }
          } else {
            setColumnVisible(ReportGridService.getColId(col), visible);
          }
        }
      });

      if (reportGrid.autoSizeColumns.id === ReportGridService.SIZING_PAGE.id) {
        //hack to make sure all the space is redistributed
        this.setColumnSizing(gridId, ReportGridService.SIZING_HEADER);
        this.setColumnSizing(gridId, ReportGridService.SIZING_PAGE);
      }

      // resize the header when the column visibility is changed
      this.resizeGridHeaderHeight(gridId);
    });

  };

  public updateColumnsOrder(gridId:string):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    _.chain(reportGrid.reportMetaData.columns)
      .filter((col:IColumnMetaData) => col.formatting.showinlist)
      .sortBy((col:IColumnMetaData) => col.listSequence)
      .forEach((col:IColumnMetaData, idx:number) => {
        const id:string = ReportGridService.getColId(col);
        reportGrid.api?.moveColumns([id], idx);
      });
  }

  private static columnVisible(col:IColumnMetaData, currentBP:string):boolean {
    const formatting:IColumnFormatting = col.formatting || {};
    switch (currentBP) {
      case 'xs':
        return u(formatting.xs);
      case 'xsm':
        return u(formatting.xs) || u(formatting.xsm); //TODO KW add this breakpoint
      case 'sm':
        return u(formatting.xs) || u(formatting.xsm) || u(formatting.sm);
      case 'md':
        return u(formatting.xs) || u(formatting.xsm) || u(formatting.sm) || u(formatting.md);
      case 'lg':
        return u(formatting.xs) || u(formatting.xsm) || u(formatting.sm) || u(formatting.md) || u(formatting.lg);
      case 'xl':
        return u(formatting.xs) || u(formatting.xsm) || u(formatting.sm) || u(formatting.md) || u(formatting.xl);
      default:
        return true;
    }

    //undefined, null or true
    function u(prop:boolean):boolean {
      return util.isNullOrUndefined(prop) || prop;
    }
  }

  public ensureIndexVisible(gridId:string, rowIndex:number):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    if (util.notNullOrUndefinedOrEmpty(rowIndex) && rowIndex >= 0) {
      reportGrid.api?.ensureIndexVisible(rowIndex);
    }
  }

  public getIndex(gridId:number | string, key:string, valueId:number):number {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    const model:any = reportGrid.api?.getModel();
    const rows:any[] = model.rowsToDisplay;
    return _.findIndex(rows, row => row.data[ key ] === valueId);
  }

  public clickRow(gridId:string, rowIndex:number):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    if (util.notNullOrUndefinedOrEmpty(rowIndex) && rowIndex >= 0) {
      reportGrid.api?.getDisplayedRowAtIndex(rowIndex).setSelected(true, true);
    }
  }

  public installSubColumns(gridId:string, subColumns:ISubColumns, browselistActionPermission?:IBrowselistActionPermission):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);

    reportGrid.subColumns = subColumns || {};
    this.installColumns(reportGrid, browselistActionPermission);
  }

  public getReportGrid(gridId:number | string):ReportGrid {
    return this.grids[ gridId ];
  }

  public getMetaData(gridId:string):IReportMetadata {
    return this.getReportGrid(gridId).reportMetaData;
  }

  // Remove all sorting
  public clearSort(gridId:string):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    reportGrid.sortModel = [];
    this.doWhenGridReady(reportGrid, () => {
      reportGrid.api?.applyColumnState({
        defaultState: {
          sort: null
        }
      });
    });
  }

  private rowGroupingSortComparator = (valueA:any, valueB:any):number => {
    valueA = util.extractValue(valueA);
    valueB = util.extractValue(valueB);

    if (util.isNullOrUndefinedOrEmpty(valueA) && util.isNullOrUndefinedOrEmpty(valueB)) {
      return 0;
    }

    if (util.isNullOrUndefinedOrEmpty(valueA)) {
      return -1;
    }

    if (util.isNullOrUndefinedOrEmpty(valueB)) {
      return 1;
    }

    if (_.isNumber(valueA) && _.isNumber(valueB)) {
      // values will come in as number
      return valueA - valueB;
    }

    // replace comma in value, since number sometimes is formatted to (e.g. 1,200.00)
    valueA = valueA.replace(/,/g, '');
    valueB = valueB.replace(/,/g, '');

    if (!isNaN(valueA) && !isNaN(valueB)) {
      // values will come in as strings
      return Number(valueA) - Number(valueB);
    }

    // Handle month names (e.g. Jan, Feb, Mar, ...)
    const valueAOrder = SHORT_MONTHS_ORDER[valueA];
    const valueBOrder = SHORT_MONTHS_ORDER[valueB];
    if (valueAOrder !== undefined && valueBOrder !== undefined) {
      return valueAOrder - valueBOrder;
    }

    return valueA < valueB? -1 : (valueA > valueB? 1 : 0);
  };

  // Adds a sort to the specified grid
  public addSort(gridId:string, name:string, direction:GridSortType):void {
    const reportGrid = this.getReportGrid(gridId);
    reportGrid.sortModel.push({
      name,
      direction
    });

    this.doWhenGridReady(reportGrid, () => {
      // it can be that there is no metadata yet -> no way to find the column metadata
      if (reportGrid.reportMetaData && reportGrid.reportMetaData.columns) {
        const colDef:IColumnMetaData = _.findWhere(reportGrid.reportMetaData.columns, { name });
        if (colDef) {
          reportGrid.api?.applyColumnState({
            state: reportGrid.api?.getColumnState().concat({
              colId: ReportGridService.getColId(colDef),
              sort: direction
            })
          });
        }
      }
    });
  }

  public isColumnVisible(colMetaData:IColumnMetaData):boolean {
    // colMetaData.formatting.defaultinlist - checkbox for column's visibility
    // colMetaData.formatting.showinlist - columns not shown at all
    return colMetaData.formatting.defaultinlist && colMetaData.formatting.showinlist;
  }
  
  public resetGridState(gridId:string):void {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    reportGrid.api.resetColumnState();
    reportGrid.api.resetColumnGroupState();
    reportGrid.api.setGridOption('pivotMode', false);
  }

  public setEditRowFunction = (gridId:string, editRowFn:Function):void => {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    reportGrid.editRow = editRowFn;
  };

  public setDeleteRowFunction = (gridId:string, deleteRowFn:Function):void => {
    const reportGrid:ReportGrid = this.getReportGrid(gridId);
    reportGrid.deleteRow = deleteRowFn;
  };

  private notifyLayoutSettingChange(gridId:string):void {
    this.onLayoutSettingChangeSubject.next(gridId);
  }

  // allow to have a variation of rows in browselist, some rows selectable others not (no checkboxes)
  private isRowSelectable = (node:RowNode | ICellRendererParams, browseListConfig?:IBrowseListConfig):boolean => {
    const idKey:string = browseListConfig.dataBindings?.idKey;
    return !(idKey && util.isNullOrUndefinedOrEmpty(node.data?.[ idKey ]));
  };
}